import classNames from "classnames";
import { Accordion, BorderedAccordionItem } from "components/Accordion";
import Checkbox from "components/Checkbox";
import Popover from "components/Popover";
import AskPaperFilterTooltip from "components/Popover/AskPaperFilterTooltip/AskPaperFilterTooltip";
import StudyTypesFilterTooltip from "components/Popover/StudyTypesFilterTooltip/StudyTypesFilterTooltip";
import Switch from "components/Switch";
import { BetaTag } from "components/Tag/tags/BetaTag";
import { ALL_DOMAINS } from "constants/filters";
import { StudyType } from "helpers/api";
import pluralize from "helpers/pluralize";
import { countries } from "helpers/sharedDataConstants";
import { StudyTypesFilterOptions } from "helpers/studyTypes";
import useLabels from "hooks/useLabels";
import { Filter, Filters } from "store/slices/search";
import CountryInput from "./CountryInput";
import DomainInput from "./DomainInput";
import DurationInput from "./DurationInput";
import { JournalRankSelect } from "./JournalRankSelect";
import NumberInput from "./NumberInput";
import { PublishYearFilter } from "./PublishYearFilter";
import { AskPaperIcon, OpenAccessIcon, PreprintIcon } from "design_system";

const SWITCH_CONTAINER_CLASSNAME = "flex flex-row  w-full justify-between";

type FilterChangeHandler = <T extends Filter>(
  filter: T,
  value: Filters[T]
) => void;
interface FiltersFormProps {
  pendingFilters: Filters;
  onFilterChange: FilterChangeHandler;
}

interface FilterSectionProps {
  pendingFilters: Filters;
  changePendingFilter: FilterChangeHandler;
}

function FiltersForm({ pendingFilters, onFilterChange }: FiltersFormProps) {
  const [filterLabels] = useLabels("filter");

  const changePendingFilter = <T extends Filter>(
    filter: T,
    value: Filters[T]
  ) => {
    onFilterChange(filter, value);
  };

  const accordionItems = [
    {
      itemKey: "general",
      title: filterLabels["general"],
      subtitle: filterLabels["general-desc"],
      content: (
        <GeneralSection
          pendingFilters={pendingFilters}
          changePendingFilter={changePendingFilter}
        />
      ),
    },
    {
      itemKey: "methodology",
      title: filterLabels["methodology"],
      subtitle: filterLabels["methodology-desc"],
      content: (
        <MethodologySection
          pendingFilters={pendingFilters}
          changePendingFilter={changePendingFilter}
        />
      ),
    },
    {
      itemKey: "domain",
      title: filterLabels["domain"],
      subtitle: pluralize(filterLabels["domain-desc"], {
        RESEARCH_DOMAINS_COUNT: ALL_DOMAINS.length,
      }),
      content: (
        <DomainInput
          pendingFilters={pendingFilters}
          changePendingFilter={changePendingFilter}
        />
      ),
    },
    {
      itemKey: "countries",
      title: filterLabels["countries"],
      subtitle: pluralize(filterLabels["countries-desc"], {
        COUNTRY_COUNT: countries.length,
      }),
      content: (
        <CountryInput
          pendingFilters={pendingFilters}
          changePendingFilter={changePendingFilter}
        />
      ),
    },
  ];

  return (
    <Accordion defaultOpen="general">
      {accordionItems.map((item) => (
        <BorderedAccordionItem key={item.itemKey} {...item} />
      ))}
    </Accordion>
  );
}

export default FiltersForm;

const GeneralSection = ({
  pendingFilters,
  changePendingFilter,
}: FilterSectionProps) => {
  const [filterLabels] = useLabels("filter");
  return (
    <div className="flex flex-col gap-4">
      <div>
        <span className="block mb-2 sm-md">{filterLabels["publish-year"]}</span>
        <PublishYearFilter
          onChange={changePendingFilter}
          yearMin={pendingFilters.yearMin}
          yearMax={pendingFilters.yearMax}
        />
      </div>
      <JournalRankSelect
        onSelectChange={(quartile) => {
          changePendingFilter("sjrBestQuartileMax", quartile);
        }}
        sjrMax={pendingFilters.sjrBestQuartileMax}
      />
      <div className="w-full">
        <span className="block mb-2 sm-md">{filterLabels["citations"]}</span>
        <NumberInput
          value={pendingFilters.citationsMin}
          onChange={(value) => changePendingFilter("citationsMin", value)}
          placeholder="0"
          size="sm"
          leftAddon={
            <span className="mr-3 inline-block w-14 text-fg-muted">
              At least
            </span>
          }
        />
      </div>

      <div className={classNames(SWITCH_CONTAINER_CLASSNAME, "items-center")}>
        <div className="flex items-center gap-3">
          <PreprintIcon />
          <span className="sm-md">{filterLabels["exclude-preprints"]}</span>
        </div>
        <Switch
          active={Boolean(pendingFilters.excludePreprints)}
          onChange={(value) =>
            changePendingFilter("excludePreprints", value ? true : undefined)
          }
        />
      </div>
      <div className={classNames(SWITCH_CONTAINER_CLASSNAME, "items-start")}>
        <div className="flex items-center gap-3">
          <AskPaperIcon />
          <span className="flex items-center gap-2 sm-md">
            {filterLabels["ask-paper"]}
            <Popover tooltipContent={<AskPaperFilterTooltip />}>
              <BetaTag />
            </Popover>
          </span>
        </div>
        <Switch
          active={Boolean(pendingFilters.hasValidChatPdf)}
          onChange={(value) =>
            changePendingFilter("hasValidChatPdf", value ? true : undefined)
          }
        />
      </div>
      <div className={classNames(SWITCH_CONTAINER_CLASSNAME, "items-center")}>
        <div className="flex items-center gap-3">
          <OpenAccessIcon />
          <span className="sm-md">{filterLabels["has-open-access-pdf"]}</span>
        </div>
        <Switch
          active={Boolean(pendingFilters.openAccess)}
          onChange={(value) =>
            changePendingFilter("openAccess", value ? true : undefined)
          }
        />
      </div>
    </div>
  );
};

const MethodologySection = ({
  pendingFilters,
  changePendingFilter,
}: FilterSectionProps) => {
  const [filterLabels] = useLabels("filter");

  const toggleStudyType = (studyType: StudyType) => {
    const updatedStudyTypes = pendingFilters.studyTypes ?? [];

    changePendingFilter(
      "studyTypes",
      updatedStudyTypes.includes(studyType)
        ? updatedStudyTypes.filter((type) => type !== studyType) // Remove if already selected
        : [...updatedStudyTypes, studyType] // Add if not selected
    );
  };
  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="flex flex-row items-center gap-2 mb-2 ">
          <span className="sm-bold">{filterLabels["design"]}</span>
          <Popover
            interactive
            maxWidth={334}
            tooltipContent={<StudyTypesFilterTooltip />}
          >
            <i className="icon-info text-fg-muted" />
          </Popover>
        </div>
        <div className="flex flex-col gap-2">
          {StudyTypesFilterOptions.map((option, optionIdx) => {
            const isSelected = Boolean(
              pendingFilters.studyTypes?.includes(option.value)
            );
            return (
              <div key={optionIdx} className="flex gap-3">
                <Checkbox
                  isChecked={isSelected}
                  onToggle={() => toggleStudyType(option.value)}
                />
                {option.elementTag}
              </div>
            );
          })}
        </div>
      </div>
      <div className="w-full">
        <span className="block mb-2 sm-md">{filterLabels["sample-size"]}</span>
        <NumberInput
          value={pendingFilters.sampleSizeMin}
          onChange={(value) => changePendingFilter("sampleSizeMin", value)}
          placeholder="1"
          size="sm"
          leftAddon={
            <span className="mr-3 w-14 inline-block text-fg-muted">
              At least
            </span>
          }
        />
      </div>

      <DurationInput
        pendingFilters={pendingFilters}
        changePendingFilter={changePendingFilter}
      />

      <div className={classNames(SWITCH_CONTAINER_CLASSNAME, "items-center")}>
        <span className="sm-md">{filterLabels["controlled-studies"]}</span>
        <Switch
          active={Boolean(pendingFilters.filterControlledStudies)}
          onChange={(value) =>
            changePendingFilter(
              "filterControlledStudies",
              value ? true : undefined
            )
          }
        />
      </div>
      <div className={classNames(SWITCH_CONTAINER_CLASSNAME, "items-center")}>
        <span className="sm-md">{filterLabels["human-studies"]}</span>
        <Switch
          active={Boolean(pendingFilters.filterHumanStudies)}
          onChange={(value) =>
            changePendingFilter("filterHumanStudies", value ? true : undefined)
          }
        />
      </div>
    </div>
  );
};
